import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import useAuth from "../hooks/useAuth";
import useQuery from "../hooks/useQuery";
import { useProject } from "./project";

const AssetsVariablesContext = React.createContext(null);

export const AssetsVariablesProvider = ({ children }) => {
  const { user } = useAuth();
  const { projectId } = useParams();
  const currentProject = useProject();
  const { settings } = user.company;

  const hideDepletedAssets =
    currentProject?.settings?.onsite_capture_to_warehouse_storage ||
    currentProject?.storageEnabled !== true
      ? false
      : settings.receiving_wizard;

  const warehouseDropdownEnabled = settings.warehouse_id_dropdown_menu;
  const searchParams = useQuery();
  const warehouseId = searchParams.get("warehouseId");
  const costCenterId = searchParams.get("costCenterId");
  const inventoryFilter = searchParams.get("inventory");

  const hasImportedAssets =
    (settings.receiving_wizard && !projectId) ||
    currentProject?.importedAssets ||
    currentProject?.referenceAssets ||
    false;

  const getWarehouseId = () => {
    if (warehouseId === "all") {
      return undefined;
    }
    if (warehouseId) {
      return warehouseId;
    }
    if (warehouseDropdownEnabled && currentProject?.warehouses?.length > 0) {
      return "all";
    }
    return undefined;
  };

  const getInitialInventoryState = () => {
    if (inventoryFilter === "all") {
      return { hideDepletedAssets: false, hasImportedAssets };
    }
    if (inventoryFilter === "in_stock") {
      return { hideDepletedAssets: true };
    }
    if (inventoryFilter === "out_of_warehouse") {
      return { showDeployedAssets: true };
    }
    if (inventoryFilter === "store_page_scope") {
      return { hideDepletedAssets: true, storePage: true };
    }

    return {
      hideDepletedAssets,
      ...(hideDepletedAssets === false && { hasImportedAssets }),
    };
  };

  const [variables, setVariables] = useState({
    ...getInitialInventoryState(),
    ...(warehouseDropdownEnabled &&
      currentProject?.warehouses?.length > 0 &&
      getWarehouseId() !== "all" && {
        warehouseId: getWarehouseId(),
      }),
    ...(costCenterId && { costCenterId }),
  });

  useEffect(() => {
    setVariables(prev => {
      const warehouseIdValue = getWarehouseId();
      const newVariables = { ...prev };
      if (warehouseIdValue === "all") {
        delete newVariables.warehouseId;
      } else {
        newVariables.warehouseId = warehouseIdValue;
      }

      if (inventoryFilter === "all") {
        delete newVariables.showDeployedAssets;
        delete newVariables.storePage;
        newVariables.hideDepletedAssets = false;
        newVariables.hasImportedAssets = hasImportedAssets;
      } else if (inventoryFilter === "in_stock") {
        delete newVariables.showDeployedAssets;
        delete newVariables.storePage;
        delete newVariables.hasImportedAssets;
        newVariables.hideDepletedAssets = true;
      } else if (inventoryFilter === "out_of_warehouse") {
        delete newVariables.hideDepletedAssets;
        delete newVariables.hasImportedAssets;
        delete newVariables.storePage;
        newVariables.showDeployedAssets = true;
      } else if (inventoryFilter === "store_page_scope") {
        delete newVariables.showDeployedAssets;
        delete newVariables.hasImportedAssets;
        newVariables.hideDepletedAssets = true;
        newVariables.storePage = true;
      }

      if (costCenterId) {
        newVariables.costCenterId = costCenterId;
      } else {
        delete newVariables.costCenterId;
      }

      return newVariables;
    });
  }, [warehouseId, costCenterId, currentProject?.id, inventoryFilter, hasImportedAssets]);

  return (
    <AssetsVariablesContext.Provider value={{ variables, hasImportedAssets, setVariables }}>
      {children}
    </AssetsVariablesContext.Provider>
  );
};

export const useAssetsVariables = () => React.useContext(AssetsVariablesContext);
